import { authErrorMessage, isRecoverable } from "@auth/authErrorMessage";
import { useClinic } from "@clinic/clinicQueries";
import { AuthClipboard } from "@components/AuthClipboard";
import { useAuth } from "@context/AuthContext";
import { useUser } from "@context/UserContext";
import { Button, cn, IconButton } from "@scribenote/brian-design-system";
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";

const CODE_LEN = 2;
const PIN_LEN = 4;
const TOTAL = CODE_LEN + PIN_LEN;

const GENERIC_ERROR = "That code or PIN didn't match. Try again.";
// TODO(WEB-3130): confirm the real support destination with the team.
const SUPPORT_HREF = "mailto:support@scribenote.com";

/** Eye for the PIN reveal toggle; slashed while the PIN is revealed. */
const EyeIcon = ({ slashed }: { slashed: boolean }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4"
  >
    <path d="M1.6 8C3.1 5.1 5.3 3.6 8 3.6S12.9 5.1 14.4 8c-1.5 2.9-3.7 4.4-6.4 4.4S3.1 10.9 1.6 8Z" />
    <circle cx="8" cy="8" r="2.1" />
    {slashed && <path d="M3 13.5 13 2.5" />}
  </svg>
);

/**
 * Second auth stage: the user enters their 2-digit per-clinic code + 4-digit
 * PIN as segmented boxes. It auto-submits the instant the last digit lands
 * (`/auth/select-user`). On a recoverable failure we keep the code, clear the
 * PIN, and refocus; the message stays generic since the backend won't say
 * which was wrong. A deactivated account is terminal: the boxes stay disabled
 * and focus moves to the support link. The clinic name (and, once the backend
 * exposes it, logo) brands the screen via the authenticated GET /auth/clinic.
 */
export const AccessCodeScreen = () => {
  const { submitAccessCode } = useUser();
  const { logout } = useAuth();
  const { data: clinic } = useClinic();

  const [digits, setDigits] = useState<string[]>(() => Array(TOTAL).fill(""));
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryFocus, setRetryFocus] = useState(false);
  const [locked, setLocked] = useState(false);
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const supportRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!retryFocus || submitting) return;
    refs.current[CODE_LEN]?.focus();
    setRetryFocus(false);
  }, [retryFocus, submitting]);

  // Submitting disables the focused box, so the browser has already dropped
  // focus to <body> by the time we lock. Park it on the only action left.
  useEffect(() => {
    if (locked) supportRef.current?.focus();
  }, [locked]);

  const focusBox = (i: number) => refs.current[i]?.focus();

  const submit = async (all: string[]) => {
    setSubmitting(true);
    try {
      await submitAccessCode(
        all.slice(0, CODE_LEN).join(""),
        all.slice(CODE_LEN).join(""),
      );
      // Success unmounts this screen (the user-scope session opens the portal).
    } catch (err) {
      setError(authErrorMessage(err, GENERIC_ERROR));
      // Keep the code (their username), wipe the PIN, then refocus once inputs
      // are enabled again. Disabled inputs cannot receive focus.
      setDigits((prev) => prev.map((d, i) => (i < CODE_LEN ? d : "")));
      // Nothing to retry on a terminal failure: leave the boxes disabled so a
      // stray keystroke can't clear the explanation and auto-submit again.
      if (isRecoverable(err)) setRetryFocus(true);
      else setLocked(true);
    } finally {
      setSubmitting(false);
    }
  };

  const apply = (next: string[]) => {
    setDigits(next);
    if (error) setError(null);
    if (next.every((d) => d !== "")) void submit(next);
  };

  const handleChange = (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    const ch = raw ? raw[raw.length - 1] : "";
    const next = digits.map((d, idx) => (idx === i ? ch : d));
    if (ch && i < TOTAL - 1) focusBox(i + 1);
    apply(next);
  };

  const handleKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      setDigits((prev) => prev.map((d, idx) => (idx === i - 1 ? "" : d)));
      focusBox(i - 1);
    }
  };

  const handlePaste = (i: number) => (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    const next = [...digits];
    for (let k = 0; k < pasted.length && i + k < TOTAL; k++)
      next[i + k] = pasted[k];
    focusBox(Math.min(i + pasted.length, TOTAL - 1));
    apply(next);
  };

  // A digit cell: a ruled paper cell on the sheet, filled in mono ink.
  const box = (i: number, masked: boolean) => (
    <input
      key={i}
      ref={(el) => {
        refs.current[i] = el;
      }}
      type={masked && !reveal ? "password" : "text"}
      inputMode="numeric"
      autoComplete="off"
      maxLength={1}
      value={digits[i]}
      onChange={handleChange(i)}
      onKeyDown={handleKeyDown(i)}
      onPaste={handlePaste(i)}
      onFocus={(e) => e.currentTarget.select()}
      disabled={submitting || locked}
      aria-label={
        masked ? `PIN digit ${i - CODE_LEN + 1}` : `User code digit ${i + 1}`
      }
      data-testid={masked ? `access-pin-${i - CODE_LEN}` : `access-code-${i}`}
      className={cn(
        "h-12 w-9 rounded-paper border bg-card text-center font-mono text-xl text-ink",
        "sm:h-14 sm:w-12 sm:text-2xl",
        "transition duration-150 ease-paper",
        "focus-visible:border-accent focus-visible:outline-none",
        "disabled:cursor-not-allowed disabled:bg-recess disabled:text-faint",
        error ? "border-ink-stopped" : "border-rule",
      )}
    />
  );

  return (
    <AuthClipboard>
      {/* The letterhead: whose sheet this is. */}
      <p className="border-b border-rule pb-3 text-center text-annotation-caps text-annotation">
        {clinic?.name ?? "Brian"} · Account portal
      </p>

      <div className="mt-5 text-center">
        <h1 className="font-display text-xl font-medium tracking-tight text-ink">
          Enter your access code
        </h1>
        <p className="mt-2 text-sm text-ink-body">
          Your 2-digit user code, then your 4-digit PIN. I'll open your portal
          when the last digit lands.
        </p>
      </div>

      <div className="mt-7 flex items-end justify-center gap-2 sm:gap-3">
        <div className="flex flex-col gap-2">
          {/* h-7 matches the PIN header row so both labels sit level. */}
          <span className="flex h-7 items-center text-annotation-caps text-annotation">
            User code
          </span>
          <div className="flex gap-1.5 sm:gap-2">
            {Array.from({ length: CODE_LEN }, (_, k) => box(k, false))}
          </div>
        </div>

        <span
          className="pb-3 text-xl text-annotation sm:pb-4 sm:text-2xl"
          aria-hidden="true"
        >
          ·
        </span>

        <div className="flex flex-col gap-2">
          <div className="flex h-7 items-center justify-between">
            <span className="text-annotation-caps text-annotation">PIN</span>
            <IconButton
              size="sm"
              variant="ghost"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? "Hide PIN" : "Show PIN"}
              aria-pressed={reveal}
              data-testid="access-reveal"
            >
              <EyeIcon slashed={reveal} />
            </IconButton>
          </div>
          <div className="flex gap-1.5 sm:gap-2">
            {Array.from({ length: PIN_LEN }, (_, k) => box(CODE_LEN + k, true))}
          </div>
        </div>
      </div>

      {/* Fixed-height status slot: error or the auto-submit progress line. */}
      <div className="mt-4 min-h-5 text-center text-sm" role="status">
        {error ? (
          <span className="text-ink-stopped" data-testid="access-error">
            {error}
          </span>
        ) : submitting ? (
          <span className="text-annotation">Opening your portal…</span>
        ) : null}
      </div>

      <p className="mt-4 text-center text-sm text-ink-body">
        Trouble getting in?{" "}
        <a
          ref={supportRef}
          href={SUPPORT_HREF}
          className="rounded-paper font-medium text-accent-deep hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Let me help.
        </a>
      </p>

      <div className="mt-6 border-t border-rule pt-4">
        <Button
          variant="ghost"
          onClick={() => logout()}
          data-testid="access-logout"
          className="w-full"
        >
          Log out
        </Button>
      </div>
    </AuthClipboard>
  );
};
